/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/ProjectOverviewDayZeroComponent" />
import * as React from "react";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Card } from "VSSUI/Card";
import { Image } from "VSSUI/Image";
import { css } from "VSSUI/Util";

import { ITfsPageData, ITfsPageService } from "Tfs/Platform/Page";
import { Constants, PerformanceConstants, TelemetryConstants } from "TFS/Views/ProjectOverviewPage/Constants";
import { ComponentRegionNames, ImagePaths, OverviewTabTelemetryConstants } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { getImageLocation } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Utils";
import * as Resources from "TFS/Views/ProjectOverviewPage/Resources";
import { StoreLoadingState } from "TFS/Views/ProjectOverviewPage/Shared/Contracts";
import { CurrentUserInfoStore } from "TFS/Views/ProjectOverviewPage/Shared/CurrentUserInfoStore";
import { CurrentUserPermission } from "TFS/Views/ProjectOverviewPage/Shared/Models";

export interface ProjectOverviewDayZeroComponentProps extends IVssComponentProperties {
    currentUserInfoStore: CurrentUserInfoStore;
}

export interface ProjectOverviewDayZeroComponentState {
    manageServiceLink: string | undefined;
    servicesCount: number;
}

export class ProjectOverviewDayZeroComponent extends VssComponent<ProjectOverviewDayZeroComponentProps, ProjectOverviewDayZeroComponentState> {
    private _projectName: string | undefined;
    private _ttiMarked: boolean;

    constructor(props: ProjectOverviewDayZeroComponentProps, context: IVssComponentContext) {
        super(props, context);

        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const childComponents = layoutManager.getComponentsForRegion(ComponentRegionNames.ProjectOverviewDayZeroNavRegion);
        const servicesCount = childComponents.length;

        const tfsPageService: ITfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData: ITfsPageData | undefined = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this._projectName = tfsPageData.project.name;
        }

        const currentUserInfoStoreState = this.props.currentUserInfoStore.getState();
        const manageServiceLink = this._getManageServiceLink(currentUserInfoStoreState.currentUserPermission);

        this.state = {
            servicesCount: servicesCount,
            manageServiceLink: manageServiceLink
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.props.currentUserInfoStore.addChangedListener(this._onCurrentUserInfoStoreChange);

        this._markTimeToInteractiveIfNeeded();
    }

    public componentDidUpdate(prevProps: ProjectOverviewDayZeroComponentProps, prevState: ProjectOverviewDayZeroComponentState): void {
        super.componentDidUpdate(prevProps, prevState);

        this._markTimeToInteractiveIfNeeded();
    }

    public componentWillUnmount(): void {
        this.props.currentUserInfoStore.removeChangedListener(this._onCurrentUserInfoStoreChange);

        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        const { servicesCount, manageServiceLink } = this.state;

        return (
            <Card className={css("project-overview-day-zero flex-center", this.props.className)} aria-label={Resources.ZeroDayCardAriaLabel}>
                <div className={"flex-column"}>
                    <Image
                        containImage={true}
                        className="po-day-zero-image"
                        src={getImageLocation(this.context.pageContext, ImagePaths.ProjectOverviewDayZeroImagePath)}
                        alt=""
                    />
                    <div className="po-day-zero-text-container flex-column flex-center justify-center">
                        <h1 className="title-l po-day-zero-title">{Resources.ProjectOverviewDayZero_Title}</h1>
                        {servicesCount > 0 && (
                            <>
                                <div className="sub-title font-size-m">
                                    {servicesCount === 1
                                        ? Resources.ProjectOverviewDayZero_SubTitle_OneService
                                        : Resources.ProjectOverviewDayZero_SubTitle_TwoOrMoreServices}
                                </div>
                                <RegionComponent
                                    className="navigation-container flex-row justify-center"
                                    componentRegion={ComponentRegionNames.ProjectOverviewDayZeroNavRegion}
                                    childComponentProperties={{ className: "service-link-container font-size-m font-weight-semibold" }}
                                />
                            </>
                        )}
                        {manageServiceLink && (
                            <FPSLink
                                className="manage-services-link link-text font-size-m"
                                href={manageServiceLink}
                                telemetrySource={OverviewTabTelemetryConstants.DayZero_ManageServiceLink}
                            >
                                {servicesCount > 0
                                    ? Resources.ProjectOverviewDayZero_OrManageYourServiceText
                                    : Resources.ProjectOverviewDayZero_ManageYourServiceText}
                            </FPSLink>
                        )}
                    </div>
                </div>
            </Card>
        );
    }

    private _getManageServiceLink(currentUserPermission: CurrentUserPermission | undefined): string | undefined {
        if (currentUserPermission && currentUserPermission.isAdmin && this._projectName) {
            return this.context.pageContext
                .getService<IVssLocationService>("IVssLocationService")
                .routeUrl("ms.vss-admin-web.project-admin-hub-route", {
                    project: this._projectName
                });
        }

        return undefined;
    }

    private _onCurrentUserInfoStoreChange = (): void => {
        const { currentUserPermission, loadingState } = this.props.currentUserInfoStore.getState();
        this.setState({
            manageServiceLink: this._getManageServiceLink(currentUserPermission)
        });
    };

    private _markTimeToInteractiveIfNeeded = (): void => {
        if (!this._ttiMarked && this.props.currentUserInfoStore.getState().loadingState !== StoreLoadingState.Loading) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Constants.Area, PerformanceConstants.ProjectOverviewTtiFeatureName, undefined, {
                source: "day-zero-component"
            });

            this._ttiMarked = true;
        }
    };
}
